import { Plus, Trash2 } from "lucide-react";
import ReviewItems from "@/components/draft/ReviewItems";

const inp = "w-full bg-transparent border border-transparent hover:border-border focus:border-blue-500 focus:bg-slate-900 rounded-md px-2 py-1 outline-none transition-colors";

function Section({ title, children }) {
  return <div className="pt-4"><div className="text-xs font-bold uppercase tracking-wider text-blue-300 border-b border-border pb-1 mb-2">{title}</div>{children}</div>;
}

function BulletRow({ b, onText, onRemove, locked, id }) {
  const n = b.text.length;
  const bad = n < 120 || n > 220;
  return (
    <div className="flex gap-2 items-start group">
      <span className="mt-2 text-slate-500">•</span>
      <textarea data-testid={id} disabled={locked} value={b.text} onChange={(e) => onText(e.target.value)} rows={2}
        className={`${inp} text-sm resize-none leading-snug ${b.text.includes("[ASK CLIENT") ? "text-amber-200" : ""}`} />
      <span className={`font-mono text-[10px] mt-2 w-8 text-right ${bad ? "text-amber-400" : "text-slate-500"}`}>{n}</span>
      {!locked && <button onClick={onRemove} className="mt-2 text-slate-600 opacity-0 group-hover:opacity-100 hover:text-rose-400"><Trash2 size={12} /></button>}
    </div>
  );
}

export default function DraftEditor({ content: c, onChange, bank, locked, onDecide }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  const setH = (k, v) => set("header", { ...c.header, [k]: v });
  const setRole = (i, r) => set("experience", c.experience.map((x, j) => (j === i ? r : x)));
  return (
    <div className="space-y-4">
      <ReviewItems c={c} onDecide={onDecide} locked={locked} />
      <div data-testid="resume-draft-editor" className="rounded-xl border border-border bg-[#0F1520] px-8 py-6">
        <input disabled={locked} value={c.header.name} onChange={(e) => setH("name", e.target.value)} className={`${inp} text-2xl font-bold font-display`} data-testid="draft-name" />
        <div className="grid grid-cols-3 gap-1 text-sm text-slate-300">
          {["location_line", "phone", "email", "linkedin", "portfolio"].map((k) => (
            <input key={k} disabled={locked} placeholder={k} value={c.header[k] || ""} onChange={(e) => setH(k, e.target.value)} className={inp} data-testid={`draft-header-${k}`} />
          ))}
        </div>
        <input disabled={locked} value={c.work_authorization} onChange={(e) => set("work_authorization", e.target.value)} className={`${inp} text-sm text-emerald-300`} data-testid="draft-work-auth" />
        <input disabled={locked} value={c.headline} onChange={(e) => set("headline", e.target.value)} className={`${inp} font-semibold text-blue-200`} data-testid="draft-headline" />
        <Section title="Summary">
          <textarea disabled={locked} value={c.summary} onChange={(e) => set("summary", e.target.value)} rows={3} className={`${inp} text-sm`} data-testid="draft-summary" />
          <input disabled={locked} value={(c.years_statements || []).join("; ")} onChange={(e) => set("years_statements", e.target.value.split(";").map((x) => x.trim()).filter(Boolean))}
            className={`${inp} text-xs text-slate-400`} placeholder="Years statements, separated by ;" data-testid="draft-years" />
        </Section>
        <Section title="Skills">
          {c.skills_groups.map((g, i) => (
            <div key={i} className="flex gap-2 text-sm">
              <input disabled={locked} value={g.category} onChange={(e) => set("skills_groups", c.skills_groups.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))} className={`${inp} w-48 font-semibold`} />
              <input disabled={locked} value={g.items.join(", ")} onChange={(e) => set("skills_groups", c.skills_groups.map((x, j) => (j === i ? { ...x, items: e.target.value.split(",").map((s) => s.trim()) } : x)))} className={inp} data-testid={`draft-skills-${i}`} />
            </div>
          ))}
        </Section>
        <Section title="Experience">
          {c.experience.map((r, i) => {
            const spare = bank.filter((b) => b.role_id === r.role_id && b.status === "spare" && !r.bullets.some((x) => x.text === b.text));
            return (
              <div key={r.role_id} className="mb-4" data-testid={`draft-role-${r.role_id}`}>
                <div className="flex gap-2 text-sm items-center">
                  <input disabled={locked} value={r.title} onChange={(e) => setRole(i, { ...r, title: e.target.value })} className={`${inp} font-bold w-56`} />
                  <input disabled={locked} value={r.company} onChange={(e) => setRole(i, { ...r, company: e.target.value })} className={`${inp} w-44`} />
                  <input disabled={locked} value={r.company_descriptor} placeholder="US-terms descriptor" onChange={(e) => setRole(i, { ...r, company_descriptor: e.target.value })} className={`${inp} text-slate-400 flex-1`} />
                  <span className="font-mono text-xs text-slate-400 whitespace-nowrap">{r.start} – {r.end}</span>
                </div>
                {r.bullets.map((b, k) => (
                  <BulletRow key={k} b={b} locked={locked} id={`draft-bullet-${r.role_id}-${k}`}
                    onText={(t) => setRole(i, { ...r, bullets: r.bullets.map((x, j) => (j === k ? { ...x, text: t, char_count: t.length } : x)) })}
                    onRemove={() => setRole(i, { ...r, bullets: r.bullets.filter((_, j) => j !== k) })} />
                ))}
                {!locked && spare.length > 0 && (
                  <select value="" data-testid={`draft-bank-select-${r.role_id}`} className="ml-5 mt-1 bg-slate-900 border border-border rounded-md text-xs px-2 py-1 max-w-[90%]"
                    onChange={(e) => { const b = spare.find((x) => x.id === e.target.value); if (b) setRole(i, { ...r, bullets: [...r.bullets, { text: b.text, keywords_used: b.keywords_used, fact_ids: b.fact_ids, char_count: b.text.length }] }); }}>
                    <option value="">+ Add a bullet from the bullet bank ({spare.length} spare)</option>
                    {spare.map((b) => <option key={b.id} value={b.id}>{b.text.slice(0, 140)}</option>)}
                  </select>
                )}
                {!locked && <button onClick={() => setRole(i, { ...r, bullets: [...r.bullets, { text: "", keywords_used: [], fact_ids: [], char_count: 0 }] })} className="ml-5 mt-1 text-xs text-slate-500 hover:text-slate-200 inline-flex items-center gap-1"><Plus size={11} />blank bullet</button>}
              </div>
            );
          })}
        </Section>
        <Section title="Education">
          {c.education.map((e, i) => (
            <div key={i} className="flex gap-2 text-sm">
              {["degree", "school", "end", "detail"].map((k) => (
                <input key={k} disabled={locked} value={e[k] || ""} placeholder={k} className={inp}
                  onChange={(ev) => set("education", c.education.map((x, j) => (j === i ? { ...x, [k]: ev.target.value } : x)))} />
              ))}
            </div>
          ))}
        </Section>
        <Section title="Certifications">
          <textarea disabled={locked} value={c.certifications.join("\n")} onChange={(e) => set("certifications", e.target.value.split("\n"))} rows={2} className={`${inp} text-sm`} />
        </Section>
      </div>
    </div>
  );
}
